import { useCallback, useEffect, useMemo } from 'react'

import { signal } from '@preact-signals/safe-react'

import { apiRegistry, pendingRequests, requestMetrics } from './state'
import {
  ApiError,
  ApiRegistryItem,
  CreateFetchDataOptions,
  FetchState,
  RequestMetrics,
  SignalFetchApiConfig,
  UseApiParams
} from './types'
import {
  checkUnresolved,
  computeUrl,
  createThrottledFetch,
  extractErrorDetails,
  fetchWithRetry,
  getRequestKey,
  isCancel,
  isParamsMatch,
  isValidCache,
  stableStringify
} from './utils'

export class FetchApiServiceFactory {
  private errorMiddlewares: Array<(error: unknown) => unknown> = []
  private requestMiddlewares: Array<
    (config: SignalFetchApiConfig) => SignalFetchApiConfig
  > = []
  private responseMiddlewares: Array<(response: unknown) => unknown> = []

  static create(): FetchApiServiceFactory {
    return new FetchApiServiceFactory()
  }

  /**
   * Refresh API by key and optional parameters.
   * @param apiKey API unique identifier.
   * @param options Optional filter parameters.
   * @returns Promise array of refresh requests.
   */
  async refreshApi<F = unknown, U = unknown>(
    apiKey: string,
    options?: {
      urlParams?: U
      fetchParams?: F
    }
  ): Promise<unknown[]> {
    const registryItems = apiRegistry.get(apiKey)
    if (!registryItems || registryItems.size === 0) {
      console.warn(`No registered instances found for API key: ${apiKey}`)
      return []
    }

    const matchedItems = Array.from(registryItems).filter((item) => {
      const urlParamsMatch = isParamsMatch(item.urlParams, options?.urlParams)
      const fetchParamsMatch = isParamsMatch(
        item.fetchParams,
        options?.fetchParams
      )
      return urlParamsMatch && fetchParamsMatch
    })

    if (matchedItems.length === 0) {
      console.warn(
        `No matching instances found for API key: ${apiKey} with the specified parameters`
      )
      return []
    }

    const refreshPromises = matchedItems.map((item) => item.fetch())
    return Promise.all(refreshPromises)
  }

  /**
   * Batch refresh multiple APIs
   */
  async batchRefreshApi(
    requests: Array<{
      apiKey: string
      options?: {
        urlParams?: unknown
        fetchParams?: unknown
      }
    }>
  ): Promise<unknown[][]> {
    return Promise.all(
      requests.map(({ apiKey, options }) => this.refreshApi(apiKey, options))
    )
  }

  /**
   * Get collected request metrics
   */
  getRequestMetrics(): RequestMetrics[] {
    return [...requestMetrics]
  }

  /**
   * Clear request metrics
   */
  clearRequestMetrics(): void {
    requestMetrics.length = 0
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addErrorMiddleware(middleware: (error: any) => any): void {
    this.errorMiddlewares.push(middleware)
  }

  addRequestMiddleware(
    middleware: (config: SignalFetchApiConfig) => SignalFetchApiConfig
  ): void {
    this.requestMiddlewares.push(middleware)
  }

  addResponseMiddleware(middleware: (response: unknown) => unknown): void {
    this.responseMiddlewares.push(middleware)
  }

  private applyErrorMiddlewares(error: unknown): unknown {
    return this.errorMiddlewares.reduce(
      (err, middleware) => middleware(err),
      error
    )
  }

  private applyRequestMiddlewares(
    config: SignalFetchApiConfig
  ): SignalFetchApiConfig {
    return this.requestMiddlewares.reduce(
      (conf, middleware) => middleware(conf),
      config
    )
  }

  private applyResponseMiddlewares(response: unknown): unknown {
    return this.responseMiddlewares.reduce(
      (res, middleware) => middleware(res),
      response
    )
  }

  create<T = unknown, F = never, U = never>(
    initialUrl: string,
    config?: SignalFetchApiConfig
  ) {
    // Create shared state
    const apiKey = config?.key || initialUrl
    const method = config?.method ?? 'GET'

    const data = signal<T | null>(null)
    const isLoading = signal<boolean>(false)
    const error = signal<ApiError | null>(null)

    // Shared state object
    const state: FetchState<T> = { data, isLoading, error }

    // Create fetch data with instance middlewares
    const { fetchData, cancelRequest } = this.createFetchData<T, F>({
      initialUrl,
      config,
      method,
      state
    })

    // Create Hook version
    const useFetch = function (params: UseApiParams<F, U> = {}) {
      const memoUrlParams = useMemo(
        () => params.urlParams,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [stableStringify(params.urlParams)]
      )
      const memoFetchParams = useMemo(
        () => params.fetchParams,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [stableStringify(params.fetchParams)]
      )

      const fetch = useCallback(
        (
          overrides: Partial<
            SignalFetchApiConfig & {
              fetchParams?: Partial<F>
              urlParams?: Partial<U>
            }
          > = {}
        ): Promise<T> => {
          const computedUrl = computeUrl(
            initialUrl,
            overrides?.urlParams
              ? {
                  ...memoUrlParams,
                  ...overrides.urlParams
                }
              : memoUrlParams,
            config?.baseURL
          )
          const unresolved = checkUnresolved(computedUrl)
          if (unresolved) return unresolved

          return fetchData({
            customConfig: overrides,
            fetchParams: memoFetchParams
              ? ({ ...memoFetchParams, ...overrides.fetchParams } as F)
              : (overrides.fetchParams as F),
            url: computedUrl
          })
        },
        [memoFetchParams, memoUrlParams]
      )

      useEffect(() => {
        if (params.isInit) {
          fetch()
        }
        if (method !== 'GET') return
        const registryItem: ApiRegistryItem = {
          fetch,
          urlParams: memoUrlParams,
          fetchParams: memoFetchParams
        }
        if (!apiRegistry.has(apiKey)) {
          apiRegistry.set(apiKey, new Set())
        }
        apiRegistry.get(apiKey)?.add(registryItem)
        return () => {
          const registryItems = apiRegistry.get(apiKey)
          if (registryItems) {
            registryItems.forEach((item) => {
              if (item.fetch === fetch) {
                registryItems.delete(item)
              }
            })
            if (registryItems.size === 0) {
              apiRegistry.delete(apiKey)
            }
          }
          cancelRequest()
        }
      }, [fetch, memoFetchParams, memoUrlParams, params.isInit])

      return {
        data,
        isLoading,
        error,
        fetch,
        cancelRequest
      }
    }

    // Create api client
    const fetchClient = function (params: UseApiParams<F, U> = {}) {
      const fetch = (
        overrides: Partial<
          SignalFetchApiConfig & {
            fetchParams?: Partial<F>
            urlParams?: Partial<U>
          }
        > = {}
      ): Promise<T> => {
        const computedUrl = computeUrl(
          initialUrl,
          overrides?.urlParams
            ? {
                ...params.urlParams,
                ...overrides.urlParams
              }
            : params.urlParams,
          config?.baseURL
        )
        const unresolved = checkUnresolved(computedUrl)
        if (unresolved) return unresolved

        return fetchData({
          customConfig: overrides,
          fetchParams: params.fetchParams
            ? ({ ...params.fetchParams, ...overrides.fetchParams } as F)
            : (overrides.fetchParams as F),
          url: computedUrl
        })
      }

      return {
        data,
        isLoading,
        error,
        fetch,
        cancelRequest
      }
    }

    // Add key property to Hook
    useFetch.key = apiKey

    // Return object containing both modes
    return {
      useFetch,
      fetchClient,
      key: apiKey
    }
  }

  private createFetchData<T, F>({
    initialUrl,
    config,
    method,
    state
  }: CreateFetchDataOptions<T>) {
    let abortController = new AbortController()

    const fetchData = async ({
      customConfig,
      fetchParams,
      url
    }: {
      customConfig?: Partial<SignalFetchApiConfig>
      fetchParams?: F
      url?: string
    }): Promise<T> => {
      const computedUrl = url ?? initialUrl
      const mergedConfig = { ...config, ...customConfig }

      // Generate unique request key
      const requestKey = getRequestKey(computedUrl, fetchParams)

      // Setup metrics
      const metrics: RequestMetrics = {
        startTime: Date.now(),
        url: computedUrl,
        method
      }

      // Check cache
      if (
        mergedConfig.cache &&
        method === 'GET' &&
        pendingRequests.has(requestKey)
      ) {
        const cachedItem = pendingRequests.get(requestKey)!

        if (isValidCache(cachedItem)) {
          metrics.cacheHit = true
          metrics.endTime = Date.now()
          metrics.duration = metrics.endTime - metrics.startTime
          metrics.success = true
          requestMetrics.push(metrics)

          if (cachedItem.data !== undefined) {
            return cachedItem.data as T
          }

          return cachedItem.promise
        } else if (mergedConfig.cache.staleWhileRevalidate) {
          const staleData = cachedItem.data

          if (staleData !== undefined) {
            fetchData({
              customConfig: {
                ...customConfig,
                skipBackground: true
              },
              fetchParams,
              url
            }).catch(() => {
              // Ignore background refresh errors
            })

            metrics.cacheHit = true
            metrics.endTime = Date.now()
            metrics.duration = metrics.endTime - metrics.startTime
            metrics.success = true
            requestMetrics.push(metrics)

            return staleData as T
          }
        }

        pendingRequests.delete(requestKey)
      }

      if (pendingRequests.has(requestKey)) {
        metrics.cacheHit = true
        requestMetrics.push(metrics)
        return pendingRequests.get(requestKey)!.promise
      }

      state.isLoading.value = true
      state.error.value = null
      abortController = new AbortController()

      // Apply instance request middlewares
      const processedConfig = this.applyRequestMiddlewares({
        ...mergedConfig,
        url: initialUrl,
        method,
        data:
          method !== 'GET'
            ? {
                ...fetchParams,
                ...mergedConfig.data
              }
            : mergedConfig.data,
        params:
          method === 'GET'
            ? {
                ...fetchParams,
                ...mergedConfig.params
              }
            : mergedConfig.params
      })

      const fullUrlBase = computeUrl(
        computedUrl,
        undefined,
        processedConfig.baseURL
      )

      let fullUrl = fullUrlBase
      if (method === 'GET' && processedConfig.params) {
        const searchParams = new URLSearchParams()
        Object.entries(
          processedConfig.params as Record<string, unknown>
        ).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (typeof value === 'object') {
              searchParams.append(key, JSON.stringify(value))
            } else {
              searchParams.append(key, String(value))
            }
          }
        })
        const queryString = searchParams.toString()
        if (queryString) {
          fullUrl = `${fullUrlBase}${
            fullUrlBase.includes('?') ? '&' : '?'
          }${queryString}`
        }
      }

      const fetchOptions: RequestInit = {
        method,
        signal: abortController.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(processedConfig?.headers || {})
        },
        credentials: processedConfig.credentials || 'include'
      }

      if (['POST', 'PUT', 'PATCH'].includes(method) && processedConfig.data) {
        fetchOptions.body = JSON.stringify(processedConfig.data)
      }

      const handleRequest = async () => {
        const response = await fetch(fullUrl, fetchOptions)

        if (!response.ok) {
          const errorText = await response.text()
          let errorData
          try {
            errorData = JSON.parse(errorText)
          } catch (e) {
            errorData = errorText
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const error: any = new Error(response.statusText || 'Request failed')
          error.response = {
            data: errorData,
            status: response.status,
            statusText: response.statusText
          }
          throw error
        }

        const contentType = response.headers.get('content-type')
        let data: T

        if (contentType && contentType.includes('application/json')) {
          data = await response.json()
        } else if (mergedConfig.responseType === 'blob') {
          data = (await response.blob()) as unknown as T
        } else if (mergedConfig.responseType === 'arraybuffer') {
          data = (await response.arrayBuffer()) as unknown as T
        } else {
          data = (await response.text()) as unknown as T
        }

        // Apply instance response middlewares
        data = this.applyResponseMiddlewares(data) as T

        state.data.value = data
        return data
      }

      let requestPromise: Promise<T>

      if (mergedConfig.retry && mergedConfig.retry.times > 0) {
        requestPromise = fetchWithRetry(
          handleRequest,
          mergedConfig.retry.times,
          mergedConfig.retry.delay
        )
      } else {
        requestPromise = handleRequest()
      }

      if (mergedConfig.throttle) {
        const throttledFetch = createThrottledFetch(
          () => requestPromise,
          mergedConfig.throttle
        )
        requestPromise = throttledFetch()
      }

      const promise = requestPromise
        .then((data) => {
          metrics.success = true

          if (mergedConfig.cache && method === 'GET') {
            const cacheItem = pendingRequests.get(requestKey)
            if (cacheItem) {
              cacheItem.data = data
            }
          }

          return data
        })
        .catch((err) => {
          metrics.success = false
          if (isCancel(err)) {
            // Ignore cancel errors
            return {} as T
          } else {
            // Apply instance error middlewares
            const processedError = this.applyErrorMiddlewares(err)
            state.error.value = extractErrorDetails(processedError)
            throw processedError
          }
        })
        .finally(() => {
          metrics.endTime = Date.now()
          metrics.duration = metrics.endTime - metrics.startTime
          requestMetrics.push(metrics)

          if (!mergedConfig.skipBackground) {
            state.isLoading.value = false
          }

          const current = pendingRequests.get(requestKey)
          if (current && current.promise === promise) {
            pendingRequests.delete(requestKey)
          }
        })

      if (method === 'GET') {
        pendingRequests.set(requestKey, {
          promise,
          timestamp: Date.now(),
          ttl: mergedConfig.cache?.ttl
        })
      }

      return promise
    }

    return {
      fetchData,
      cancelRequest: () => {
        abortController.abort()
        abortController = new AbortController()
      }
    }
  }
}

export type { SignalFetchApiConfig } from './types'
